import { useEffect, useRef, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { SpellCheck2, X } from "lucide-react";

const LS_KEY = "kadana_spellcheck_enabled";

// Debounced, dismissible spelling & grammar suggestions. Never blocks or alters the text without an
// explicit click. A per-user on/off toggle is persisted in localStorage (respected across sessions).
// The browser calls Kadana's own /lt proxy — never LanguageTool directly.
export default function SpellCheck({ text, onApply, apiPath = "/lt/check", debounceMs = 500 }) {
  const [enabled, setEnabled] = useState(() => {
    try { return localStorage.getItem(LS_KEY) !== "off"; } catch { return true; }
  });
  const [matches, setMatches] = useState([]);
  const [source, setSource] = useState("");
  const [density, setDensity] = useState(() => { try { return localStorage.getItem("kadana_lt_density") || "default"; } catch { return "default"; } });
  const [ignored, setIgnored] = useState({});
  const timer = useRef(null);

  const toggle = () => {
    setEnabled((v) => {
      const nv = !v;
      try { localStorage.setItem(LS_KEY, nv ? "on" : "off"); } catch { /* ignore */ }
      if (!nv) setMatches([]);
      return nv;
    });
  };

  const run = useCallback((t) => {
    api.post(apiPath, { text: t, language: "en-GB", level: density })
      .then((r) => { setMatches(r.data.matches || []); setSource(r.data.source || ""); })
      .catch(() => { setMatches([]); });
  }, [apiPath, density]);

  useEffect(() => {
    if (!enabled) return;
    if (timer.current) clearTimeout(timer.current);
    const t = text || "";
    if (!t.trim()) { setMatches([]); return; }
    timer.current = setTimeout(() => run(t), debounceMs);   // fires once, 500ms after typing stops
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [text, enabled, debounceMs, run]);

  const keyOf = (m) => `${m.offset}:${m.length}:${m.ruleId}`;
  const visible = matches.filter((m) => !ignored[keyOf(m)]);

  const accept = (m, replacement) => {
    const t = text || "";
    const next = t.slice(0, m.offset) + replacement + t.slice(m.offset + m.length);
    onApply(next);                       // explicit user action — never auto-applied
    setIgnored((g) => ({ ...g, [keyOf(m)]: true }));
  };
  const ignore = (m) => setIgnored((g) => ({ ...g, [keyOf(m)]: true }));

  return (
    <div data-testid="spellcheck" style={{ marginTop: 8 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button type="button" className="k-btn k-btn-ghost" data-testid="spellcheck-toggle" onClick={toggle}
          style={{ padding: "4px 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 5 }}>
          <SpellCheck2 size={13} /> Spelling &amp; grammar: <strong>{enabled ? "on" : "off"}</strong>
        </button>
        {enabled && (
          <select className="k-control" data-testid="spellcheck-density" aria-label="Spelling &amp; grammar density"
            value={density} onChange={(e) => { setDensity(e.target.value); try { localStorage.setItem("kadana_lt_density", e.target.value); } catch { /* ignore */ } }}
            style={{ width: "auto", padding: "3px 6px", fontSize: 12 }}>
            <option value="default">Standard</option>
            <option value="picky">Picky</option>
          </select>
        )}
        {enabled && visible.length > 0 && (
          <span className="k-muted" data-testid="spellcheck-count" style={{ fontSize: 12 }}>
            {visible.length} suggestion{visible.length === 1 ? "" : "s"} — you can still submit as-is
          </span>
        )}
      </div>
      {enabled && visible.length > 0 && (
        <div data-testid="spellcheck-suggestions" style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 6 }}>
          {visible.slice(0, 8).map((m) => (
            <div key={keyOf(m)} className="k-container" style={{ padding: "8px 10px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span style={{ fontSize: 12, flex: "1 1 200px" }}>
                <span className="mono" style={{ textDecoration: "underline wavy", textDecorationColor: "var(--danger,#ef4444)" }}>
                  {(text || "").slice(m.offset, m.offset + m.length) || "—"}
                </span>
                <span className="k-muted"> — {m.shortMessage || m.message}</span>
              </span>
              {m.replacements.slice(0, 3).map((rep) => (
                <button key={rep} type="button" className="k-btn k-btn-ghost" data-testid="spellcheck-accept"
                  onClick={() => accept(m, rep)} style={{ padding: "3px 9px", fontSize: 12 }}>{rep}</button>
              ))}
              <button type="button" className="k-btn k-btn-ghost" data-testid="spellcheck-ignore"
                onClick={() => ignore(m)} style={{ padding: "3px 7px" }} aria-label="Ignore suggestion"><X size={12} /></button>
            </div>
          ))}
          {source === "external_fallback" && (
            <p className="k-muted" style={{ fontSize: 11 }}>Checked via the admin-configured external fallback.</p>
          )}
        </div>
      )}
    </div>
  );
}
